import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { navigate, openAiOverlayOrNavigate, OVERLAY_ONLY } from "../App";
import { getPricing, formatEuro, type Package as PricingPackage } from "@/data/pricing";
import { ProductAnchor } from "./ProductAnchor";

const BRAND = "#FF3C00";

type Service = { name: string; tagline: string; desc: string; bullets: string[] };
type Principle = { n: string; title: string; body: string };
type Step = { n: string; label: string; title: string; body: string };
type Stat = { n: string; label: string };

const SERVICE_KEYS = ["overlay", "rework", "longterm"] as const;
const SERVICE_HREFS: Record<(typeof SERVICE_KEYS)[number], string> = {
  overlay: "/services/ai-overlay",
  rework: "/services/website-rework",
  longterm: "/services/langzeit-betreuung",
};
const MORE_PRODUCT_SLUGS = ["cate", "one-click-deploy", "sight", "skyline", "foody"] as const;
const MORE_PRODUCT_NAMES: Record<(typeof MORE_PRODUCT_SLUGS)[number], string> = {
  cate: "CATE",
  "one-click-deploy": "Open CLI Deployment",
  sight: "Sight",
  skyline: "Skyline Spotter",
  foody: "Foody",
};

export function ProductsSection() {
  const { t } = useTranslation("home");
  const { t: tc } = useTranslation("common");
  const { t: tg } = useTranslation("overlayGate");

  // When the AI_OVERLAY_ONLY gate is on, we don't sell website building/rework,
  // so the "Website Rework" offering is dropped from the services line-up.
  const serviceKeys = OVERLAY_ONLY ? SERVICE_KEYS.filter((k) => k !== "rework") : SERVICE_KEYS;
  const lead = OVERLAY_ONLY ? tg("products.lead") : t("products.lead");
  const gridColsClass = serviceKeys.length === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3";

  return (
    <section id="products" className="py-24 md:py-32">
      <div className="px-5 sm:px-8 md:px-[66px]">
        <SectionTitle>{t("products.title")}</SectionTitle>
        <SectionLead>{lead}</SectionLead>
      </div>

      <div className="mt-16 h-px bg-grey-800" />
      <div className={`grid grid-cols-1 ${gridColsClass}`}>
        {serviceKeys.map((k, i) => {
          const s = t(`products.services.${k}`, { returnObjects: true }) as Service;
          const href = SERVICE_HREFS[k];
          const n = String(i + 1).padStart(2, "0");
          return (
            <a
              key={k}
              href={href}
              onClick={(e) => { e.preventDefault(); navigate(href); }}
              className="group flex flex-col p-6 lg:p-8
                         border-t border-grey-800 first:border-t-0
                         lg:border-t-0 lg:border-l lg:first:border-l-0 lg:border-grey-800
                         transition-colors hover:bg-[rgba(255,60,0,0.06)]"
            >
              <div className="text-sm font-mono text-grey-500 mb-4">{n}</div>
              <h3 className="text-2xl font-bold text-white tracking-tight mb-2 leading-tight">{s.name}</h3>
              <p className="text-[14px] text-grey-400 leading-snug mb-5">{s.tagline}</p>
              <p className="text-[14px] text-grey-300 leading-relaxed mb-6">{s.desc}</p>
              <ul className="flex flex-col gap-1.5 mb-6">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-[13px] text-grey-400 leading-snug">
                    <span className="font-bold mt-0.5 shrink-0 text-xs" style={{ color: BRAND }}>✓</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <span className="mt-auto inline-flex items-center gap-1.5 text-sm text-grey-300 group-hover:text-white transition-colors">
                {tc("learnMore")} <span>→</span>
              </span>
            </a>
          );
        })}
      </div>
      <div className="h-px bg-grey-800" />

      <div className="mt-20 md:mt-24 px-5 sm:px-8 md:px-[66px]">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <h3 className="text-xl md:text-2xl font-bold text-white tracking-tight">{t("products.moreHeading")}</h3>
          <a
            href="/products"
            onClick={(e) => { e.preventDefault(); navigate("/products"); }}
            className="text-sm font-medium text-grey-200 hover:text-white transition-colors"
          >
            {t("products.moreLink")} →
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {MORE_PRODUCT_SLUGS.map((slug) => (
            <ProductAnchor
              key={slug}
              slug={slug}
              className="group flex items-center justify-between gap-3 p-4 transition-colors hover:bg-grey-900/40"
              style={{ border: "1px solid #27272a" }}
            >
              <div className="min-w-0">
                <div className="text-sm font-bold text-white tracking-tight truncate">{MORE_PRODUCT_NAMES[slug]}</div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-grey-500 truncate">
                  {t(`products.moreProducts.${slug}`)}
                </div>
              </div>
              <span className="text-[11px] font-mono text-grey-600 group-hover:text-grey-200 transition-colors shrink-0">→</span>
            </ProductAnchor>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CateSection() {
  const { t } = useTranslation("home");
  const bullets = t("cate.bullets", { returnObjects: true }) as string[];

  return (
    <section className="py-24 md:py-32 overflow-hidden">
      <div className="px-5 sm:px-8 md:px-[66px]">
        <div className="text-[11px] font-mono uppercase tracking-widest text-grey-500 mb-4">
          {t("cate.eyebrow")}
        </div>
        <SectionTitle>{t("cate.title")}</SectionTitle>
        <SectionLead>{t("cate.lead")}</SectionLead>
      </div>

      <div className="mt-12 md:mt-16 px-5 sm:px-8 md:px-[66px] grid grid-cols-1 md:grid-cols-2 gap-3">
        <a
          href="/products/cate"
          onClick={(e) => { e.preventDefault(); navigate("/products/cate"); }}
          className="group block relative rounded-2xl overflow-hidden"
          style={{ border: "1px solid #27272a" }}
        >
          <img
            src="/assets/cate-screenshot-dark.png"
            alt="CATE in dark mode with terminals, editors and browsers"
            className="w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
          />
          <div className="absolute bottom-0 left-0 right-0 px-4 pb-3 pt-8 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(22,23,28,0.8) 0%, transparent 100%)" }}>
            <span className="text-[11px] font-mono text-grey-400">Dark</span>
          </div>
        </a>
        <a
          href="/products/cate"
          onClick={(e) => { e.preventDefault(); navigate("/products/cate"); }}
          className="group block relative rounded-2xl overflow-hidden"
          style={{ border: "1px solid #27272a" }}
        >
          <img
            src="/assets/cate-screenshot-light.png"
            alt="CATE in light mode with terminals, editors and browsers"
            className="w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
          />
          <div className="absolute bottom-0 left-0 right-0 px-4 pb-3 pt-8 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(22,23,28,0.8) 0%, transparent 100%)" }}>
            <span className="text-[11px] font-mono text-grey-400">Light</span>
          </div>
        </a>
      </div>

      <div className="mt-10 md:mt-12 px-5 sm:px-8 md:px-[66px]">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {bullets.map((b) => (
            <div
              key={b}
              className="flex items-start gap-3 p-4"
              style={{ border: "1px solid #27272a" }}
            >
              <span className="font-bold mt-0.5 shrink-0 text-xs" style={{ color: BRAND }}>✓</span>
              <span className="text-[14px] text-grey-300 leading-snug">{b}</span>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a
            href="/products/cate"
            onClick={(e) => { e.preventDefault(); navigate("/products/cate"); }}
            className="inline-flex items-center gap-2 text-white text-sm font-medium px-7 py-2.5 rounded-full transition-colors"
            style={{ backgroundColor: BRAND }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e63600")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND)}
          >
            {t("cate.cta")}
          </a>
          <a
            href="https://github.com/0-AI-UG/cate"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-grey-200 text-sm font-medium px-7 py-2.5 rounded-full transition-colors border border-grey-700 hover:border-grey-500 hover:text-white"
          >
            {t("cate.github")}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="opacity-60">
              <path d="M1 11L11 1M11 1H4M11 1V8" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}

export function WhySection() {
  const { t } = useTranslation("home");
  const principles = t("why.principles", { returnObjects: true }) as Principle[];

  return (
    <section id="why" className="relative py-24 md:py-32 overflow-hidden">
      <div
        aria-hidden
        className="hidden md:block absolute select-none pointer-events-none font-bold leading-none"
        style={{
          top: 40,
          right: -10,
          fontSize: 320,
          color: "rgba(255,60,0,0.05)",
          fontFamily: "var(--font-display)",
        }}
      >
        “
      </div>

      <div className="relative px-5 sm:px-8 md:px-[66px]">
        <SectionTitle>
          {t("why.title")}
          <span className="text-grey-500">{t("why.titleAccent")}</span>
        </SectionTitle>
        <SectionLead>{t("why.lead")}</SectionLead>
      </div>

      <div className="relative mt-16 md:mt-20">
        <div className="h-px bg-grey-800" />
        {principles.map((p) => (
          <div
            key={p.n}
            className="grid grid-cols-[auto_1fr] md:grid-cols-[140px_minmax(0,1fr)_minmax(0,1.6fr)] gap-6 md:gap-10
                       px-5 sm:px-8 md:px-[66px] py-10 md:py-12
                       transition-colors hover:bg-[rgba(255,60,0,0.04)]
                       border-b border-grey-800 last:border-b-0"
          >
            <div className="text-4xl md:text-6xl font-extrabold tracking-tight leading-none" style={{ color: BRAND }}>
              {p.n}
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white tracking-tight leading-tight self-start md:self-center">
              {p.title}
            </h3>
            <p className="text-grey-400 text-[15px] leading-relaxed col-span-2 md:col-span-1 self-center">{p.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function HowSection() {
  const { t } = useTranslation("home");
  const steps = t("how.steps", { returnObjects: true }) as Step[];

  return (
    <section id="how" className="relative py-24 md:py-32 overflow-hidden">
      <div className="relative px-5 sm:px-8 md:px-[66px]">
        <SectionTitle>
          {t("how.title")}
          <span className="text-grey-500">{t("how.titleAccent")}</span>
        </SectionTitle>
        <SectionLead>{t("how.lead")}</SectionLead>
      </div>

      <div className="relative mt-16 md:mt-20">
        <div className="h-px bg-grey-800" />
        {steps.map((s) => (
          <div
            key={s.n}
            className="grid grid-cols-[auto_1fr] md:grid-cols-[140px_minmax(0,1fr)_minmax(0,1.6fr)] gap-6 md:gap-10
                       px-5 sm:px-8 md:px-[66px] py-10 md:py-12
                       transition-colors hover:bg-[rgba(255,60,0,0.04)]
                       border-b border-grey-800 last:border-b-0"
          >
            <div className="text-4xl md:text-6xl font-extrabold tracking-tight leading-none" style={{ color: BRAND }}>
              {s.n}
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white tracking-tight leading-tight self-start md:self-center">
              {s.title}
            </h3>
            <p className="text-grey-400 text-[15px] leading-relaxed col-span-2 md:col-span-1 self-center">{s.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function WhoSection() {
  const { t } = useTranslation("home");
  const stats = t("who.stats", { returnObjects: true }) as Stat[];

  return (
    <section id="who" className="px-5 sm:px-8 md:px-[66px] py-24 md:py-32">
      <SectionTitle>{t("who.title")}</SectionTitle>
      <SectionLead>{t("who.lead")}</SectionLead>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-3">
        {stats.map((s) => (
          <StatBox key={s.label} n={s.n} label={s.label} />
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-4">
        <a
          href="/about"
          onClick={(e) => { e.preventDefault(); navigate("/about"); }}
          className="text-sm font-semibold text-grey-200 hover:text-white transition-colors"
        >
          {t("who.links.about")} →
        </a>
        <span className="text-grey-700">·</span>
        <a
          href="/open-source"
          onClick={(e) => { e.preventDefault(); navigate("/open-source"); }}
          className="text-sm font-semibold text-grey-200 hover:text-white transition-colors"
        >
          {t("who.links.opensource")} →
        </a>
        <span className="text-grey-700">·</span>
        <a
          href="/work"
          onClick={(e) => { e.preventDefault(); navigate("/work"); }}
          className="text-sm font-semibold text-grey-200 hover:text-white transition-colors"
        >
          {t("who.links.work")} →
        </a>
      </div>
    </section>
  );
}

export function PricingTeaserSection() {
  const { t, i18n } = useTranslation("home");
  const { t: tai } = useTranslation("ai");
  const { t: tg } = useTranslation("overlayGate");
  const lang = i18n.language?.startsWith("en") ? "en" : "de";
  const pricing = useMemo(() => getPricing(lang), [lang]);

  const teaserFeatures = (pkg: PricingPackage) =>
    pkg.features.filter((f) => !f.toLowerCase().includes("plus:")).slice(0, 3);

  return (
    <section id="pricing" className="py-24 md:py-32">
      <div className="px-5 sm:px-8 md:px-[66px]">
        <SectionTitle>{OVERLAY_ONLY ? tg("pricing.teaserTitle") : t("pricing.title")}</SectionTitle>
        <SectionLead>{OVERLAY_ONLY ? tg("pricing.teaserLead") : t("pricing.lead")}</SectionLead>
      </div>

      <div className="mt-16 h-px bg-grey-800" />
      {OVERLAY_ONLY ? (
        // Overlay-only mode: no website-building packages — preview the AI layer.
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {pricing.kiPlans.map((plan) => (
            <a
              key={plan.id}
              href={`/pricing?ki=${plan.id}`}
              data-ai-overlay-query={tai("openPricingPrompt")}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); openAiOverlayOrNavigate(tai("openPricingPrompt"), `/pricing?ki=${plan.id}`); }}
              className="group flex flex-col p-6 lg:p-8 border-t border-grey-800 first:border-t-0
                         lg:border-t-0 lg:border-l lg:first:border-l-0 lg:border-grey-800
                         transition-colors hover:bg-[rgba(255,60,0,0.06)]"
              style={{ backgroundColor: plan.popular ? "rgba(255,60,0,0.03)" : undefined }}
            >
              <div className="flex items-center justify-between gap-3 mb-3">
                <span className="text-sm font-bold text-white">{plan.name}</span>
                {plan.popular && (
                  <span className="text-[11px] font-mono" style={{ color: BRAND }}>{t("pricing.popular")}</span>
                )}
              </div>
              <div className="flex items-baseline mb-5">
                <span className="text-2xl font-extrabold" style={{ color: BRAND }}>€{formatEuro(plan.monthlyPrice, lang)}</span>
                <span className="text-[11px] text-grey-500 ml-1">{tg("pricing.perMonth")}</span>
              </div>
              <ul className="flex flex-col gap-1.5 mb-6">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[13px] text-grey-400 leading-snug">
                    <span className="font-bold mt-0.5 shrink-0 text-xs" style={{ color: BRAND }}>✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <span className="mt-auto inline-flex items-center gap-1.5 text-sm text-grey-300 group-hover:text-white transition-colors">
                {t("pricing.details")} <span>→</span>
              </span>
            </a>
          ))}
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-3">
        {pricing.packages.map((pkg) => {
          const isCustom = pkg.basePrice === null;
          return (
            <a
              key={pkg.id}
              href={`/pricing?package=${pkg.id}`}
              data-ai-overlay-query={tai("openPricingPrompt")}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); openAiOverlayOrNavigate(tai("openPricingPrompt"), `/pricing?package=${pkg.id}`); }}
              className="group flex flex-col p-6 lg:p-8
                         border-t border-grey-800 first:border-t-0
                         lg:border-t-0 lg:border-l lg:first:border-l-0 lg:border-grey-800
                         transition-colors hover:bg-[rgba(255,60,0,0.06)]"
              style={{ backgroundColor: pkg.popular ? "rgba(255,60,0,0.03)" : undefined }}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-mono text-grey-500">{pkg.number}</span>
                {pkg.popular && (
                  <span className="text-[11px] font-mono" style={{ color: BRAND }}>{t("pricing.popular")}</span>
                )}
              </div>
              <h3 className="text-2xl font-bold text-white tracking-tight mb-2 leading-tight">{pkg.name}</h3>
              <p className="text-[14px] text-grey-400 leading-snug mb-5">{pkg.desc}</p>
              {isCustom ? (
                <>
                  <div className="text-2xl font-extrabold text-white">{t("pricing.onRequest")}</div>
                  <p className="text-[11px] text-grey-500 mt-1 mb-6">{t("pricing.onRequestNote")}</p>
                </>
              ) : (
                <>
                  <div className="text-2xl font-extrabold text-white">{t("pricing.from")}{pkg.basePrice}</div>
                  <p className="text-[11px] text-grey-500 mt-1 mb-6">{t("pricing.onceTax")}</p>
                </>
              )}
              <ul className="flex flex-col gap-1.5 mb-6">
                {teaserFeatures(pkg).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[13px] text-grey-400 leading-snug">
                    <span className="font-bold mt-0.5 shrink-0 text-xs" style={{ color: BRAND }}>✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <span className="mt-auto inline-flex items-center gap-1.5 text-sm text-grey-300 group-hover:text-white transition-colors">
                {t("pricing.details")} <span>→</span>
              </span>
            </a>
          );
        })}
      </div>
      )}
      <div className="h-px bg-grey-800" />

      <div className="mt-12 px-5 sm:px-8 md:px-[66px] text-center">
        <a
          href="/pricing"
          data-ai-overlay-query={tai("openPricingPrompt")}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openAiOverlayOrNavigate(tai("openPricingPrompt"), "/pricing"); }}
          className="inline-flex items-center gap-2 text-grey-200 text-sm font-medium px-7 py-2.5 rounded-full transition-colors border border-grey-700 hover:border-grey-500 hover:text-white"
        >
          {t("pricing.fullPricing")}
        </a>
      </div>
    </section>
  );
}

export function CtaSection() {
  const { t } = useTranslation("home");
  const { t: tc } = useTranslation("common");
  const { t: tai } = useTranslation("ai");
  return (
    <section id="contact" className="px-5 sm:px-8 md:px-[66px] py-24 md:py-32">
      <div className="max-w-2xl">
        <SectionTitle>{t("cta.title")}</SectionTitle>
        <SectionLead>{t("cta.lead")}</SectionLead>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <a
            href="/contact"
            data-ai-overlay-query={tai("openContactPrompt")}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openAiOverlayOrNavigate(tai("openContactPrompt"), "/contact"); }}
            className="inline-flex items-center gap-2 text-white text-sm font-medium px-7 py-2.5 rounded-full transition-colors"
            style={{ backgroundColor: BRAND }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e63600")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND)}
          >
            {tc("contactUs")}
          </a>
          <a
            href="/contact"
            data-ai-overlay-query={tai("openContactPrompt")}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openAiOverlayOrNavigate(tai("openContactPrompt"), "/contact"); }}
            className="inline-flex items-center gap-2 text-grey-200 text-sm font-medium px-7 py-2.5 rounded-full transition-colors border border-grey-700 hover:border-grey-500 hover:text-white"
          >
            {tc("requestProject")}
          </a>
          <a
            href="/consulting"
            onClick={(e) => { e.preventDefault(); navigate("/consulting"); }}
            className="inline-flex items-center gap-2 text-grey-300 text-sm font-medium px-3 py-2.5 transition-colors hover:text-white"
          >
            {t("cta.bookConsulting")} →
          </a>
        </div>
      </div>
    </section>
  );
}

export function HomeFooter() {
  const { t } = useTranslation("footer");
  return (
    <footer className="px-5 sm:px-8 md:px-[66px] pt-10 pb-32 md:pb-16">
      <div className="flex flex-wrap items-center justify-between gap-3 text-[11px] text-grey-500">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
          <span>© {new Date().getFullYear()} 0-AI UG</span>
          <span className="text-grey-700">·</span>
          <a
            href="/impressum"
            onClick={(e) => { e.preventDefault(); navigate("/impressum"); }}
            className="hover:text-grey-200 transition-colors"
          >
            {t("impressum")}
          </a>
          <span className="text-grey-700">·</span>
          <a
            href="/datenschutz"
            onClick={(e) => { e.preventDefault(); navigate("/datenschutz"); }}
            className="hover:text-grey-200 transition-colors"
          >
            {t("datenschutz")}
          </a>
          <span className="text-grey-700">·</span>
          <span data-consent-footer />
        </div>
        <a
          href="https://github.com/0-AI-UG"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 hover:text-grey-200 transition-colors shrink-0"
        >
          <span>GitHub</span>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="opacity-60">
            <path d="M1 11L11 1M11 1H4M11 1V8" stroke="currentColor" strokeWidth="1.2" />
          </svg>
        </a>
      </div>
    </footer>
  );
}

function SectionTitle({ children, centered }: { children: React.ReactNode; centered?: boolean }) {
  return (
    <h2
      className={`text-[32px] md:text-[44px] font-bold text-white tracking-tight mb-5 leading-[1.08] max-w-3xl ${
        centered ? "text-center mx-auto" : ""
      }`}
    >
      {children}
    </h2>
  );
}

function SectionLead({ children, centered }: { children: React.ReactNode; centered?: boolean }) {
  return (
    <p
      className={`text-grey-400 text-[15px] leading-relaxed max-w-2xl ${centered ? "mx-auto text-center" : ""}`}
    >
      {children}
    </p>
  );
}

function StatBox({ n, label }: { n: string; label: string }) {
  return (
    <div className="p-5" style={{ border: "1px solid #27272a" }}>
      <div className="text-3xl font-extrabold text-white tracking-tight mb-1">{n}</div>
      <div className="text-[12px] text-grey-400">{label}</div>
    </div>
  );
}
